// The Profiles tab's pure logic: one form field per setting, built from the
// engine's JSON Schema (GET /profiles `settingsSchema`, the pydantic settings
// model), so the fields, types and bounds always come from the API. The help
// text and grouping below only label what the schema has; a setting the
// schema gains later still gets a plain field, and one it drops disappears.
//
// No engine has a mic gate: the user can always interrupt (plan §6.3, a
// product call of 2026-10-31).

import type { EngineName, JsonSchemaProperty, Profile, SettingsSchema } from "../../services/voiceEngines";
import { KEY_PATTERN, KEY_RULE } from "./labels";

export type FieldKind = "boolean" | "integer" | "number" | "string" | "enum" | "list" | "json";

export interface FieldSpec {
  name: string;
  label: string;
  help?: string;
  group: string;
  kind: FieldKind;
  // The setting may be null: `nullLabel` is the checkbox that means null
  // (e.g. "Use each guide's speed"), `suggest` the value it starts from
  // when unticked.
  nullable: boolean;
  nullLabel?: string;
  suggest?: string;
  min?: number;
  max?: number;
  step?: number;
  minLength?: number;
  options?: string[];
}

interface FieldText {
  label: string;
  help?: string;
  group: string;
  nullLabel?: string;
  suggest?: string;
  step?: number;
}

const EL_VOICE = "Voice · set on the ElevenLabs engines";
const EL_TURNS = "Turn-taking · set on the ElevenLabs engines";
const EL_SERVER = "Our server · applied at call time";
const LIVE = "Gemini Live · applied by our server";
const CASCADE = "Cascade · applied by our server";
const OTHER = "Other settings";

// Labels and help (plan §6.3). Keyed by engine, then setting name.
const FIELD_TEXT: Partial<Record<EngineName, Record<string, FieldText>>> = {
  cascade: {
    tts_model: {
      label: "Voice model",
      group: CASCADE,
      help: "The ElevenLabs model the guide's voice speaks on. Only models ElevenLabs streams (no v3 or v4). Studio previews use cascade-default's.",
    },
    brain_model: {
      label: "Brain model",
      group: CASCADE,
      help: "The companion's Gemini model. Its first token is most of the wait before the guide speaks. Another model needs the companion evals passed on it before it leaves dev.",
    },
    brain_thinking: {
      label: "Brain thinking level",
      group: CASCADE,
      help: "Lower answers sooner. gemini-3.8 and 4 have no minimal (their lowest is low).",
    },
  },
  gemini_live: {
    model: { label: "Model", group: LIVE, help: "The Live model (VOICE_S2S_GEMINI_MODEL)." },
    silence_ms: {
      label: "End of turn after silence (ms)",
      group: LIVE,
      step: 50,
      help: "Server VAD silence before the model takes its turn. Lower answers sooner and may cut in.",
    },
    compress_after_minutes: {
      label: "Compress context after (min)",
      group: LIVE,
      help: "0 leaves it to Gemini's own trigger.",
    },
    start_sensitivity: {
      label: "Start of speech sensitivity",
      group: LIVE,
      nullLabel: "Gemini's default (high)",
      help: "Low needs clearer speech before it counts as the user talking. It does not stop speaker echo cutting the guide off (design doc §4.8).",
    },
    end_sensitivity: {
      label: "End of speech sensitivity",
      group: LIVE,
      nullLabel: "Gemini's default (high)",
      help: "Low ends the user's turn less eagerly.",
    },
    prefix_padding_ms: {
      label: "Speech before a start counts (ms)",
      group: LIVE,
      step: 50,
      nullLabel: "Gemini's default",
      help: "How long speech must last before the server VAD counts it as the user starting. Longer: fewer false starts, a later barge-in.",
    },
  },
  elevenlabs: {
    transport: {
      label: "Transport",
      group: EL_VOICE,
      help: "speech_engine: ElevenLabs Speech Engine. custom_llm: the Agents fallback (plan B).",
    },
    tts_model: { label: "TTS model", group: EL_VOICE },
    stability: { label: "Stability", group: EL_VOICE, step: 0.05 },
    similarity_boost: { label: "Similarity boost", group: EL_VOICE, step: 0.05 },
    speed: {
      label: "Speed",
      group: EL_VOICE,
      step: 0.05,
      nullLabel: "Use each guide's speed",
      suggest: "1",
      help: "From the guide's tts.speed unless set here.",
    },
    expressive_mode: { label: "Expressive mode", group: EL_VOICE, help: "v4 Turbo reads emotion from the text." },
    turn_model: { label: "Turn model", group: EL_TURNS },
    turn_eagerness: {
      label: "Turn eagerness",
      group: EL_TURNS,
      help: "Patient waits longer before answering; eager answers sooner and may cut in. The crisis gate runs on the chosen setting.",
    },
    turn_timeout_s: { label: "Re-engage after silence (s)", group: EL_TURNS, step: 0.5 },
    speculative_turn: {
      label: "Start the brain early",
      group: EL_TURNS,
      help: "Starts our reply during the pause, before ElevenLabs is sure the turn is over. Discarded attempts cost brain tokens.",
    },
    disable_first_message_interruptions: {
      label: "Protect the greeting from interruptions",
      group: EL_TURNS,
      help: "Off by default: the greeting is interruptible like every other turn. Kept so an A/B can try it.",
    },
    interruption_ignore_terms: {
      label: "Ignored as interruptions",
      group: EL_TURNS,
      nullLabel: "ElevenLabs' English and Vietnamese defaults",
      suggest: "mm-hmm, right, ừ, vâng",
      help: "Backchannels that never cut the guide off. Comma-separated.",
    },
    asr_keywords: {
      label: "Speech recognition keywords",
      group: EL_TURNS,
      nullLabel: "The guide's name",
      help: "Words the recogniser should expect. Comma-separated.",
    },
    cascade_timeout_s: {
      label: "ElevenLabs waits for our first text (s)",
      group: EL_TURNS,
      step: 0.5,
      help: "cascade_timeout_seconds: how long before ElevenLabs retries.",
    },
    brain_model: { label: "Brain model", group: EL_SERVER, help: "A model other than the companion's needs the companion evals passed on it first." },
    brain_thinking: { label: "Brain thinking level", group: EL_SERVER },
    text_chunking: { label: "Send text to the voice in", group: EL_SERVER, help: "Whole sentences, or clauses for an earlier first word." },
    filler_after_ms: {
      label: "Filler line if no text yet (ms)",
      group: EL_SERVER,
      step: 100,
      nullLabel: "Off",
      suggest: "800",
      help: "A short line such as \"Mm…\" while the brain thinks. Never on the crisis path.",
    },
  },
};

// The group order a form shows; groups the schema has no field for are skipped.
export const GROUP_ORDER = [CASCADE, LIVE, EL_VOICE, EL_TURNS, EL_SERVER, OTHER];

function humanize(name: string): string {
  const words = name.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function kindOf(p: JsonSchemaProperty): FieldKind {
  if (Array.isArray(p.enum) && p.enum.every((v) => typeof v === "string")) return "enum";
  switch (p.type) {
    case "boolean":
      return "boolean";
    case "integer":
      return "integer";
    case "number":
      return "number";
    case "string":
      return "string";
    case "array":
      return p.items?.type === "string" ? "list" : "json";
    default:
      return "json";
  }
}

// One field per schema property, in the schema's order.
export function fieldSpecs(engine: EngineName, schema: SettingsSchema): FieldSpec[] {
  const texts = FIELD_TEXT[engine] ?? {};
  return Object.entries(schema.properties ?? {}).map(([name, prop]) => {
    const variants = prop.anyOf ?? [prop];
    const nullable = variants.some((v) => v.type === "null");
    const inner = variants.find((v) => v.type !== "null") ?? prop;
    const t: FieldText | undefined = texts[name];
    const kind = kindOf(inner);
    return {
      name,
      label: t?.label ?? inner.title ?? prop.title ?? humanize(name),
      help: t?.help,
      group: t?.group ?? OTHER,
      kind,
      nullable,
      nullLabel: nullable ? (t?.nullLabel ?? "Not set") : undefined,
      suggest: t?.suggest,
      min: inner.minimum,
      max: inner.maximum,
      step: t?.step ?? (kind === "integer" ? 1 : kind === "number" ? 0.1 : undefined),
      minLength: inner.minLength,
      options: kind === "enum" ? (inner.enum as string[]) : undefined,
    };
  });
}

// ---------------------------------------------------------------- form values

// A field's value while editing: text for numbers, strings and lists (so a
// half-typed number is flagged, not lost), a boolean for a checkbox, and
// null for a nullable setting left at its null meaning.
export type FormValue = string | boolean | null;
export type FormValues = Record<string, FormValue>;

export function toForm(specs: FieldSpec[], settings: Record<string, unknown>, defaults: Record<string, unknown>): FormValues {
  const out: FormValues = {};
  for (const s of specs) {
    const v = s.name in settings ? settings[s.name] : defaults[s.name];
    if (v === null || v === undefined) {
      out[s.name] = s.nullable ? null : s.kind === "boolean" ? false : "";
    } else if (s.kind === "boolean") {
      out[s.name] = Boolean(v);
    } else if (s.kind === "list") {
      out[s.name] = Array.isArray(v) ? v.join(", ") : String(v);
    } else if (s.kind === "json") {
      out[s.name] = JSON.stringify(v);
    } else {
      out[s.name] = String(v);
    }
  }
  return out;
}

// The value a nullable field starts from when its null box is unticked.
export function startValue(spec: FieldSpec, defaults: Record<string, unknown>): FormValue {
  if (spec.kind === "boolean") return false;
  const d = defaults[spec.name];
  if (d !== null && d !== undefined) return Array.isArray(d) ? d.join(", ") : String(d);
  return spec.suggest ?? (spec.min !== undefined ? String(spec.min) : "");
}

function rangeError(spec: FieldSpec, n: number): string | null {
  if (spec.min !== undefined && spec.max !== undefined && (n < spec.min || n > spec.max)) {
    return `Between ${spec.min} and ${spec.max}.`;
  }
  if (spec.min !== undefined && n < spec.min) return `At least ${spec.min}.`;
  if (spec.max !== undefined && n > spec.max) return `At most ${spec.max}.`;
  return null;
}

// The settings to save and, per field, why a value cannot be saved.
export function fromForm(specs: FieldSpec[], form: FormValues): {
  settings: Record<string, unknown>;
  errors: Record<string, string>;
} {
  const settings: Record<string, unknown> = {};
  const errors: Record<string, string> = {};
  for (const s of specs) {
    const v = form[s.name];
    if (v === null || v === undefined) {
      if (s.nullable) settings[s.name] = null;
      else errors[s.name] = "Required.";
      continue;
    }
    if (s.kind === "boolean") {
      settings[s.name] = v === true;
      continue;
    }
    const t = String(v).trim();
    switch (s.kind) {
      case "integer":
      case "number": {
        const n = t === "" ? NaN : Number(t);
        if (!Number.isFinite(n) || (s.kind === "integer" && !Number.isInteger(n))) {
          errors[s.name] = s.kind === "integer" ? "Enter a whole number." : "Enter a number.";
          break;
        }
        const range = rangeError(s, n);
        if (range) errors[s.name] = range;
        else settings[s.name] = n;
        break;
      }
      case "enum":
        if (!s.options?.includes(t)) errors[s.name] = `One of ${s.options?.join(", ")}.`;
        else settings[s.name] = t;
        break;
      case "list":
        settings[s.name] = t
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean);
        break;
      case "json":
        try {
          settings[s.name] = JSON.parse(t);
        } catch {
          errors[s.name] = "Not valid JSON.";
        }
        break;
      default:
        if (s.minLength !== undefined && t.length < s.minLength) errors[s.name] = "Required.";
        else settings[s.name] = t;
    }
  }
  return { settings, errors };
}

// Settings compared by value, key order ignored.
export function sameSettings(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const norm = (o: Record<string, unknown>) =>
    JSON.stringify(Object.keys(o).sort().map((k) => [k, o[k]]));
  return norm(a) === norm(b);
}

// The fields whose saved value differs, for the "unsaved" summary.
export function changedFields(saved: Record<string, unknown>, next: Record<string, unknown>): string[] {
  return Object.keys(next).filter((k) => JSON.stringify(saved[k] ?? null) !== JSON.stringify(next[k] ?? null));
}

// ---------------------------------------------------------------- keys

// Why `key` cannot name a new profile, or null.
export function profileKeyError(key: string, profiles: Pick<Profile, "key" | "builtin">[]): string | null {
  const k = key.trim();
  if (!k) return "Give the profile a key.";
  if (!KEY_PATTERN.test(k)) return `A key is ${KEY_RULE}.`;
  const existing = profiles.find((p) => p.key === k);
  if (existing?.builtin) return `${k} is a built-in default's key.`;
  if (existing) return `${k} already exists.`;
  return null;
}

// A key for a copy of `key`: key-copy, key-copy-2, …
export function copyKey(key: string, profiles: { key: string }[]): string {
  const taken = new Set(profiles.map((p) => p.key));
  const base = `${key}-copy`.slice(0, 58);
  if (!taken.has(base)) return base;
  for (let n = 2; ; n++) {
    if (!taken.has(`${base}-${n}`)) return `${base}-${n}`;
  }
}

// "arm live · 2 test users", or "not used".
export function usageText(usage: Profile["usage"]): string {
  const parts = usage.arms.map((a) => `arm ${a}`);
  if (usage.overrides) parts.push(`${usage.overrides} test user${usage.overrides === 1 ? "" : "s"}`);
  return parts.join(" · ") || "not used";
}
